import InfoIcon from "@mui/icons-material/Info";
import { CardHeader, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { FC } from "react";
import { useTranslation } from "react-i18next";

export const ZorgDomeinHowToRefer: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  const steps = [
    t("Log in to ZorgDomein"),
    t("Select the appropriate referral form"),
    t("Fill in patient details"),
    t("Submit the referral"),
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        pt: mdUp ? 10 : 8,
        pb: mdUp ? "100px" : "60px",
        minHeight: "100vh",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h3"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mt: 2,
            mb: 2,
            color: theme.palette.primary.main,
          }}
        >
          {t("Referring via ZorgDomein", "Verwijzen via ZorgDomein")}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ textAlign: "center", color: "text.secondary", mb: 5 }}
        >
          {t(
            "Follow these easy steps to refer your patients via ZorgDomein",
            "Volg deze eenvoudige stappen om uw patiënten naar ons te verwijzen via ZorgDomein"
          )}
        </Typography>
        {/* 
        <Stepper
          activeStep={-1}
          orientation="vertical"
          sx={{ width: "100%", mb: 5 }}
        >
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper> */}

        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardMedia
                component="img"
                image="assets/zorgdomein/loginzorgdomein.png" // replace with actual image paths
                alt="Step 1"
                sx={{
                  objectFit: "contain",
                  p: 2,
                  borderRadius: 4,
                }}
              />
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom>
                  {t(
                    "Step 1: Log in to ZorgDomein",
                    "Stap 1: Log in op ZorgDomein"
                  )}
                </Typography>
                <Typography variant="body1">
                  {t(
                    "Visit the ZorgDomein website and log in with your healthcare provider account.",
                    "Bezoek de ZorgDomein-website en log in met uw zorgverlenersaccount."
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardMedia
                component="img"
                image="/assets/zorgdomein/overleggen.png" // replace with actual image paths
                alt="Step 2"
                sx={{
                  objectFit: "contain",
                  p: 2,
                  borderRadius: 4,
                }}
              />
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom>
                  {t("Step 2: Click Overleggen", "Stap 2: Klik op Overleggen")}
                </Typography>
                <Typography variant="body1">
                  {t(
                    "At the top of the page, click on Overleggen to start a new referral.",
                    "Klik bovenaan de pagina op Overleggen om een nieuwe verwijzing te starten."
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardMedia
                component="img"
                image="/assets/zorgdomein/inputmedprogr.png" // replace with actual image paths
                alt="Step 3"
                sx={{
                  objectFit: "contain",
                  p: 2,
                  borderRadius: 4,
                }}
              />
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom>
                  {t(
                    "Step 3: Select the medical doctor",
                    "Stap 3: Selecteer de arts"
                  )}
                </Typography>
                <Typography variant="body1">
                  {t(
                    "Type Medical Progress and select the doctor from Medical Progress.",
                    'Type "Medical Progress" en selecteer de arts bovenaan de lijst.'
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardMedia
                component="img"
                image="/assets/zorgdomein/startoverleg.png" // replace with actual image paths
                alt="Step 4"
                sx={{
                  objectFit: "contain",
                  p: 2,
                  mb: 2,
                  borderRadius: 4,
                }}
              />
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom>
                  {t(
                    "Step 4: Start a conversation",
                    "Stap 4: Start een overleg"
                  )}
                </Typography>
                <Typography variant="body1">
                  {t(
                    "Start a conversation and add relevant patient details and any additional information.",
                    "Start een overleg en beschrijf relevante patiëntgegevens."
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Box
          sx={{
            my: 3,
          }}
        >
          <Card
            sx={{
              height: "100%",
              mx: "auto",
              width: "100%",
            }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
                {t(
                  "Important information for a referral",
                  "Belangrijke gegevens voor een doorverwijzing"
                )}
              </Typography>

              <Typography variant="body1">
                {t(
                  "When referring a patient for open-source looping the following patientinformation is important:",
                  "Bij het verwijzen van een patiënt voor open source loopen zijn de volgende patiëntgegevens van belang:"
                )}
              </Typography>

              <ul style={{ textAlign: "left", color: "text.primary" }}>
                <li>
                  <Typography variant="body1">
                    {t(
                      "Type of CGM, insulin pump, and phone (Android/iPhone)",
                      "Type CGM, insulinepomp en telefoon (Android/iPhone)"
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    {t(
                      "Previous medical history that may affect the loop (thyroid problems, overweight, anxiety disorder, etc)",
                      "Relevante medische voorgeschiedenis die van invloed kan zijn op het algoritme:"
                    )}
                    <ul>
                      <li>
                        <Typography variant="body1">
                          {t(
                            "Thyroid or other metabolic problems",
                            "Schildklier of andere metabole problemen"
                          )}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body1">
                          {t("Over/underweight", "Over/ondergewicht")}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body1">
                          {t("A specific diet", "Een specifiek dieet")}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body1">
                          {t(
                            "Anxiety disorder/deprresion or other relevant mental health problems",
                            "Angststoornis/depressie of andere relevant psychische problemen"
                          )}
                        </Typography>
                      </li>
                    </ul>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    {t(
                      "Type of insulin and relevant medication and dosages",
                      "Type insuline en relevant medicatiegebruik en doseringen"
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    {t(
                      "Not yet looping, just started looping, or experienced looper",
                      "Nog niet aan het loopen, net gestart met loopen of ervaren looper"
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    {t(
                      "If just started or experienced looper, also the current issues during looping",
                      "Indien net gestart of ervaren looper dan ook de huidige problemen tijdens het loopen"
                    )}
                  </Typography>
                </li>
              </ul>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};
